/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
/* eslint-disable complexity */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Kitchen3dController } from '../../../controller';

import { ToolDefinition, ToolResult } from '../../../controller/types/tool.types';

export type ToolsPanelProps = {
	controller: Kitchen3dController;
	visible?: boolean;
	onClose?: () => void;
};

type TabId = 'parameters' | 'results';

type ResultEntry = {
	time: Date;
	toolName: string;
	result: ToolResult;
};

export const ToolsPanel = ({ controller, visible = false, onClose }: ToolsPanelProps): React.ReactElement | null => {
	const [tools, setTools] = useState<Array<ToolDefinition>>([]);
	const [selectedSource, setSelectedSource] = useState<string>('all');
	const [selectedTool, setSelectedTool] = useState<string>('');
	const [toolParams, setToolParams] = useState<Record<string, any>>({});
	const [executing, setExecuting] = useState(false);
	const [notification, setNotification] = useState<{ type: 'success' | 'warn' | 'error'; message: string } | null>(null);
	const [results, setResults] = useState<Array<ResultEntry>>([]);
	const [expandedResults, setExpandedResults] = useState<Set<number>>(new Set());
	const [activeTab, setActiveTab] = useState<TabId>('parameters');
	const [contextExpanded, setContextExpanded] = useState(false);
	const [panelWidth, setPanelWidth] = useState(400);
	const resizingRef = useRef(false);
	const startXRef = useRef(0);
	const startWidthRef = useRef(400);

	const handleResizeStart = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		resizingRef.current = true;
		startXRef.current = e.clientX;
		startWidthRef.current = panelWidth;

		const onMouseMove = (ev: MouseEvent): void => {
			if (!resizingRef.current) return;
			const delta = startXRef.current - ev.clientX;
			setPanelWidth(Math.max(400, startWidthRef.current + delta));
		};

		const onMouseUp = (): void => {
			resizingRef.current = false;
			document.removeEventListener('mousemove', onMouseMove);
			document.removeEventListener('mouseup', onMouseUp);
			document.body.style.cursor = '';
			document.body.style.userSelect = '';
		};

		document.addEventListener('mousemove', onMouseMove);
		document.addEventListener('mouseup', onMouseUp);
		document.body.style.cursor = 'ew-resize';
		document.body.style.userSelect = 'none';
	}, [panelWidth]);

	// Compute unique sources from tools
	const sources = useMemo(() => {
		const sourceSet = new Set(tools.map((t) => t.source || 'unknown'));
		return ['all', ...Array.from(sourceSet).sort()];
	}, [tools]);

	// Filter tools by selected source
	const filteredTools = useMemo(() => {
		if (selectedSource === 'all') return tools;
		return tools.filter((t) => (t.source || 'unknown') === selectedSource);
	}, [tools, selectedSource]);

	// Load tool definitions
	useEffect(() => {
		if (controller && controller.getToolDefinitions) {
			try {
				const toolDefs = controller.getToolDefinitions();
				setTools(toolDefs);
			} catch (error) {
				console.error('Error loading tool definitions:', error);
			}
		}
	}, [controller]);

	// Auto-select first tool when filtered tools change
	useEffect(() => {
		if (filteredTools.length > 0 && !filteredTools.find((t) => t.name === selectedTool)) {
			setSelectedTool(filteredTools[0].name);
		}
	}, [filteredTools, selectedTool]);

	// Helper function to get current state value for a parameter
	const getCurrentStateValue = useCallback((toolName: string, paramName: string): unknown => {
		if (!controller) return undefined; // eslint-disable-line no-undefined

		switch (toolName) {
			case 'set_render_quality':
				if (paramName === 'quality') return controller.options?.renderQuality ?? 10;
				break;
			case 'set_debug_mode':
				if (paramName === 'enabled') return controller.options?.debugMode ?? false;
				if (paramName === 'options') {
					return controller.options?.debugOptions ?? {
						cutouts: true, lights: true, camera: true, bounds: true, showTools: true
					};
				}
				break;
			case 'rotate_camera':
				try {
					const threeInstance = (controller as any).threeInstanceRef || (controller as any)['#threeInstanceRef'];
					if (threeInstance?.getCameraControls) {
						const cameraControls = threeInstance.getCameraControls();
						if (cameraControls) {
							if (paramName === 'angle') return Math.round((cameraControls.azimuthAngle * 180) / Math.PI);
							if (paramName === 'elevation') return Math.round((cameraControls.polarAngle * 180) / Math.PI);
						}
					}
				} catch { /* Camera controls not available */ }
				break;
			case 'take_screenshot':
				if (paramName === 'pixelRatio') return 2;
				break;
			default:
				break;
		}
		return undefined; // eslint-disable-line no-undefined
	}, [controller]);

	// Reset params when tool changes
	useEffect(() => {
		if (selectedTool) {
			const tool = tools.find((t) => t.name === selectedTool);
			if (tool) {
				const initialParams: Record<string, any> = {};
				const properties = tool.parameters?.properties ?? {};
				Object.entries(properties).forEach(([paramName, param]) => {
					const currentValue = getCurrentStateValue(tool.name, paramName);
					// eslint-disable-next-line no-undefined
					if (currentValue !== undefined) {
						initialParams[paramName] = currentValue;
					} else if (param.default !== undefined) { // eslint-disable-line no-undefined
						initialParams[paramName] = param.default;
					} else if (param.type === 'number') {
						initialParams[paramName] = param.min ?? 0;
					} else if (param.type === 'boolean') {
						initialParams[paramName] = false;
					} else if (param.type === 'string' && param.enum && param.enum.length > 0) {
						initialParams[paramName] = tool.parameters.required?.includes(paramName) ? param.enum[0] : '';
					} else if (param.type === 'object' && param.properties) {
						const nestedParams: Record<string, any> = {};
						Object.entries(param.properties).forEach(([nestedName, nestedParam]) => {
							if (nestedParam.type === 'boolean') nestedParams[nestedName] = true;
						});
						initialParams[paramName] = nestedParams;
					} else {
						initialParams[paramName] = '';
					}
				});
				setToolParams(initialParams);
				setNotification(null);
				setContextExpanded(false);
			}
		}
	}, [selectedTool, tools, getCurrentStateValue]);

	const handleParamChange = useCallback((paramName: string, value: any) => {
		setToolParams((prev) => ({ ...prev, [paramName]: value }));
	}, []);

	const handleExecute = useCallback(async () => {
		if (!selectedTool || !controller.executeTool) return;

		setExecuting(true);
		setNotification(null);

		try {
			const result = await controller.executeTool(selectedTool, toolParams);

			// Add to results history — auto-expand only the newest entry
			setResults((prev) => {
				const newIndex = prev.length;
				setExpandedResults(new Set([newIndex]));
				return [...prev, { time: new Date(), toolName: selectedTool, result }];
			});

			// Show notification
			if (result.success) {
				const msg = result.message || result.data ? 'Tool executed successfully' : 'Success';
				setNotification({ type: 'success', message: msg });
			} else {
				setNotification({ type: 'error', message: result.error || 'Execution failed' });
			}
		} catch (error: any) {
			const errorResult: ToolResult = { success: false, error: error?.message || 'Unknown error occurred' };
			setResults((prev) => {
				const newIndex = prev.length;
				setExpandedResults(new Set([newIndex]));
				return [...prev, { time: new Date(), toolName: selectedTool, result: errorResult }];
			});
			setNotification({ type: 'error', message: errorResult.error! });
		} finally {
			setExecuting(false);
		}
	}, [selectedTool, toolParams, controller]);

	const currentTool = filteredTools.find((t) => t.name === selectedTool);

	const copyBtnStyle: React.CSSProperties = {
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		padding: '0 2px',
		fontSize: '11px',
		lineHeight: '1',
		color: '#aaa',
		verticalAlign: 'baseline',
		fontFamily: 'inherit'
	};

	const renderDataNode = (node: unknown, depth: number): React.ReactNode => {
		const indent = '  '.repeat(depth);
		const innerIndent = '  '.repeat(depth + 1);

		if (node === null || node === undefined) return <span>{String(node)}</span>; // eslint-disable-line no-undefined

		if (typeof node !== 'object') return <span>{String(node)}</span>;

		if (Array.isArray(node)) {
			if (node.length === 0) return <span>{'[]'}</span>;
			return (
				<>
					{'[\n'}
					{node.map((item, i) => (
						<span key={i}>
							{innerIndent}
							{renderDataNode(item, depth + 1)}
							{i < node.length - 1 ? ',' : ''}
							{'\n'}
						</span>
					))}
					{indent}{']'}
				</>
			);
		}

		const entries = Object.entries(node as Record<string, unknown>);
		if (entries.length === 0) return <span>{'{}'}</span>;

		return (
			<>
				{'{\n'}
				{entries.map(([key, val], i) => (
					<span key={key}>
						{innerIndent}
						<span style={{ color: '#888' }}>{`"${key}"`}</span>
						{': '}
						{typeof val === 'string'
							? (
								<>
									<button
										onClick={() => navigator.clipboard.writeText(val).catch(() => { /* noop */ })}
										title={`Copy ${key}`}
										style={copyBtnStyle}
									>
										⧉
									</button>
									<span style={{ color: '#2e7d32' }}>{`"${val}"`}</span>
								</>
							)
							: renderDataNode(val, depth + 1)
						}
						{i < entries.length - 1 ? ',' : ''}
						{'\n'}
					</span>
				))}
				{indent}{'}'}
			</>
		);
	};

	if (!visible) return null;

	const tabStyle = (tab: TabId): React.CSSProperties => ({
		flex: 1,
		padding: '8px 0',
		border: 'none',
		borderBottom: activeTab === tab ? '2px solid #1976d2' : '2px solid transparent',
		backgroundColor: 'transparent',
		color: activeTab === tab ? '#1976d2' : '#666',
		fontWeight: activeTab === tab ? '600' : '400',
		fontSize: '13px',
		cursor: 'pointer',
		fontFamily: 'inherit'
	});

	const notificationColors: Record<string, { bg: string; border: string; text: string }> = {
		success: { bg: '#e8f5e9', border: '#4caf50', text: '#2e7d32' },
		warn: { bg: '#fff3e0', border: '#ff9800', text: '#e65100' },
		error: { bg: '#ffebee', border: '#f44336', text: '#c62828' }
	};

	return (
		<div style={{
			position: 'absolute',
			top: '10px',
			right: '10px',
			bottom: '10px',
			width: `${panelWidth}px`,
			backgroundColor: 'white',
			borderRadius: '8px',
			boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
			zIndex: 1000,
			display: 'flex',
			flexDirection: 'row',
			fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
			overflow: 'hidden'
		}}>
			{/* Resize handle */}
			<div
				onMouseDown={handleResizeStart}
				style={{
					width: '8px',
					cursor: 'ew-resize',
					flexShrink: 0,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					backgroundColor: 'transparent'
				}}
			>
				<div style={{
					width: '3px',
					height: '32px',
					borderRadius: '2px',
					backgroundColor: '#ccc'
				}} />
			</div>

			{/* Panel content */}
			<div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
			{/* Header */}
			<div style={{
				padding: '12px 16px',
				borderBottom: '1px solid #e0e0e0',
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				backgroundColor: '#f5f5f5',
				flexShrink: 0
			}}>
				<h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Tools</h3>
				{onClose && (
					<button
						onClick={onClose}
						style={{
							background: 'none',
							border: 'none',
							fontSize: '24px',
							cursor: 'pointer',
							padding: '0',
							lineHeight: '1',
							color: '#666'
						}}
					>
						×
					</button>
				)}
			</div>

			{/* Tool Selection (always visible) */}
			<div style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
				{/* Source Selection */}
				<div style={{ marginBottom: '8px' }}>
					<label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '12px', color: '#666' }}>
						Source:
					</label>
					<select
						value={selectedSource}
						onChange={(e) => setSelectedSource(e.target.value)}
						style={{ width: '100%', padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }}
					>
						{sources.map((source) => (
							<option key={source} value={source}>
								{source === 'all' ? 'All Sources' : source}
							</option>
						))}
					</select>
				</div>

				{/* Tool Selector */}
				<div>
					<label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '12px', color: '#666' }}>
						Tool:
					</label>
					<select
						value={selectedTool}
						onChange={(e) => setSelectedTool(e.target.value)}
						style={{ width: '100%', padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }}
					>
						{filteredTools.map((tool) => (
							<option key={tool.name} value={tool.name}>{tool.name}</option>
						))}
					</select>
				</div>

				{/* Agent Context (collapsible) */}
				{currentTool?.description && (
					<div style={{ marginTop: '8px' }}>
						<button
							onClick={() => setContextExpanded((prev) => !prev)}
							style={{
								background: 'none',
								border: 'none',
								padding: 0,
								cursor: 'pointer',
								fontSize: '12px',
								color: '#888',
								display: 'flex',
								alignItems: 'center',
								gap: '4px',
								fontFamily: 'inherit'
							}}
						>
							<span style={{
								display: 'inline-block',
								transition: 'transform 0.15s',
								transform: contextExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
								fontSize: '10px'
							}}>
								▶
							</span>
							Agent Context
						</button>
						{contextExpanded && (
							<div style={{ marginTop: '6px', fontSize: '12px', color: '#888', lineHeight: '1.4', paddingLeft: '14px' }}>
								{currentTool.description}
							</div>
						)}
					</div>
				)}
			</div>

			{/* Tabs */}
			<div style={{ display: 'flex', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
				<button onClick={() => setActiveTab('parameters')} style={tabStyle('parameters')}>
					Parameters
				</button>
				<button onClick={() => setActiveTab('results')} style={tabStyle('results')}>
					Results {results.length > 0 && <span style={{ fontSize: '11px', color: '#999' }}>({results.length})</span>}
				</button>
			</div>

			{/* Notification Banner */}
			{notification && (
				<div style={{
					padding: '8px 16px',
					backgroundColor: notificationColors[notification.type].bg,
					borderBottom: `1px solid ${notificationColors[notification.type].border}`,
					color: notificationColors[notification.type].text,
					fontSize: '13px',
					fontWeight: '500',
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					flexShrink: 0
				}}>
					<span>
						{notification.type === 'success' && '✓ '}
						{notification.type === 'warn' && '⚠ '}
						{notification.type === 'error' && '✗ '}
						{notification.message}
					</span>
					<button
						onClick={() => setNotification(null)}
						style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '16px', padding: '0 0 0 8px' }}
					>
						×
					</button>
				</div>
			)}

			{/* Tab Content */}
			<div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
				{activeTab === 'parameters' && (
					<>
						{/* Parameters Form */}
						{currentTool && currentTool.parameters?.properties && Object.keys(currentTool.parameters.properties).length > 0 && (
							<div style={{ marginBottom: '16px' }}>
								{Object.entries(currentTool.parameters.properties).map(([paramName, param]) => (
									<div key={paramName} style={{ marginBottom: '12px' }}>
										<label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: '500' }}>
											{paramName}
											{currentTool.parameters.required?.includes(paramName) && <span style={{ color: '#d32f2f' }}> *</span>}
											{param.description && (
												<span style={{ fontWeight: 'normal', color: '#666', fontSize: '11px', marginLeft: '8px' }}>
													({param.description})
												</span>
											)}
										</label>
										{param.type === 'number' && (
											<input
												type="number"
												value={toolParams[paramName] ?? ''}
												onChange={(e) => handleParamChange(paramName, parseFloat(e.target.value) || 0)}
												min={param.min}
												max={param.max}
												// eslint-disable-next-line no-undefined
												step={param.min !== undefined && param.max !== undefined ? (param.max - param.min) / 100 : 1}
												style={{ width: '100%', padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }}
											/>
										)}
										{param.type === 'string' && (
											param.enum
												? (
													<select
														value={toolParams[paramName] ?? ''}
														onChange={(e) => handleParamChange(paramName, e.target.value)}
														style={{ width: '100%', padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }}
													>
														{!currentTool.parameters.required?.includes(paramName) && (
															<option key="" value="">[empty]</option>
														)}
														{param.enum.map((option) => (
															<option key={option} value={option}>{option}</option>
														))}
													</select>
												)
												: (
													<input
														type="text"
														value={toolParams[paramName] ?? ''}
														onChange={(e) => handleParamChange(paramName, e.target.value)}
														placeholder={param.default ? `Default: ${param.default}` : ''}
														style={{ width: '100%', padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }}
													/>
												)
										)}
										{param.type === 'boolean' && (
											<label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
												<input
													type="checkbox"
													checked={toolParams[paramName] ?? false}
													onChange={(e) => handleParamChange(paramName, e.target.checked)}
													style={{ marginRight: '8px' }}
												/>
												<span style={{ fontSize: '13px' }}>Enabled</span>
											</label>
										)}
										{param.type === 'object' && param.properties && (
											<div style={{ padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginTop: '4px' }}>
												{Object.entries(param.properties).map(([optionName, optionParam]) => (
													<label
														key={optionName}
														style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '6px', fontSize: '13px' }}
													>
														<input
															type="checkbox"
															checked={(toolParams[paramName] ?? {})[optionName] ?? true}
															onChange={(e) => {
																const currentValue = toolParams[paramName] ?? {};
																handleParamChange(paramName, { ...currentValue, [optionName]: e.target.checked });
															}}
															style={{ marginRight: '8px' }}
														/>
														<span style={{ fontWeight: 500 }}>{optionName}</span>
														{optionParam.description && (
															<span style={{ color: '#666', fontSize: '11px', marginLeft: '8px' }}>
																({optionParam.description})
															</span>
														)}
													</label>
												))}
											</div>
										)}
									</div>
								))}
							</div>
						)}

						{/* Execute Button */}
						<button
							onClick={handleExecute}
							disabled={executing || !selectedTool}
							style={{
								width: '100%',
								padding: '10px',
								backgroundColor: executing ? '#ccc' : '#1976d2',
								color: 'white',
								border: 'none',
								borderRadius: '4px',
								fontSize: '14px',
								fontWeight: '500',
								cursor: executing || !selectedTool ? 'not-allowed' : 'pointer'
							}}
						>
							{executing ? 'Executing...' : 'Execute Tool'}
						</button>
					</>
				)}

				{activeTab === 'results' && (
					<div>
						{results.length === 0 && (
							<div style={{ color: '#999', fontSize: '13px', textAlign: 'center', padding: '24px 0' }}>
								No results yet. Execute a tool to see results here.
							</div>
						)}
						{results.slice().reverse().map((_entry, reverseIdx) => {
							const realIndex = results.length - 1 - reverseIdx;
							const entry = results[realIndex];
							const { result } = entry;
							const borderColor = result.success ? '#4caf50' : '#f44336';
							const isExpanded = expandedResults.has(realIndex);

							const resultText = [
								result.message || '',
								result.error || '',
								result.data ? JSON.stringify(result.data, null, 2) : ''
							].filter(Boolean).join('\n');

							const toggleExpand = (): void => {
								setExpandedResults((prev) => {
									const next = new Set(prev);
									if (next.has(realIndex)) {
										next.delete(realIndex);
									} else {
										next.add(realIndex);
									}
									return next;
								});
							};

							const copyToClipboard = (): void => {
								navigator.clipboard.writeText(resultText).catch(() => { /* noop */ });
							};

							const iconBtn: React.CSSProperties = {
								background: 'none',
								border: 'none',
								cursor: 'pointer',
								padding: '2px 4px',
								fontSize: '14px',
								lineHeight: '1',
								color: '#888',
								borderRadius: '3px'
							};

							return (
								<div key={realIndex} style={{
									marginBottom: '6px',
									borderRadius: '4px',
									border: `1px solid ${borderColor}`,
									backgroundColor: result.success ? '#f9fdf9' : '#fff8f8',
									overflow: 'hidden'
								}}>
									{/* Header row — always visible */}
									<div
										onClick={toggleExpand}
										style={{
											display: 'flex',
											alignItems: 'center',
											padding: '8px 10px',
											cursor: 'pointer',
											gap: '6px'
										}}
									>
										<span style={{
											display: 'inline-block',
											transition: 'transform 0.15s',
											transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
											fontSize: '10px',
											color: '#888',
											flexShrink: 0
										}}>
											▶
										</span>
										<span style={{
											fontWeight: '600',
											fontSize: '13px',
											color: result.success ? '#2e7d32' : '#c62828',
											flex: 1,
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap'
										}}>
											{result.success ? '✓' : '✗'} {entry.toolName}
										</span>
										<span style={{ fontSize: '11px', color: '#999', flexShrink: 0 }}>
											{entry.time.toLocaleTimeString()}
										</span>
										{/* Copy button */}
										<button
											onClick={(e) => { e.stopPropagation(); copyToClipboard(); }}
											title="Copy to clipboard"
											style={iconBtn}
										>
											⧉
										</button>
									</div>

									{/* Expanded body */}
									{isExpanded && (
										<div style={{ padding: '0 10px 10px 28px' }}>
											{result.message && (
												<div style={{ fontSize: '12px', color: '#333', marginTop: '2px' }}>
													{result.message}
												</div>
											)}
											{result.error && (
												<div style={{ fontSize: '12px', color: '#c62828', marginTop: '2px' }}>
													{result.error}
												</div>
											)}
											{result.data && (
												<pre style={{ margin: '4px 0 0', fontSize: '11px', overflow: 'auto', color: '#555' }}>
													{renderDataNode(result.data, 0)}
												</pre>
											)}
										</div>
									)}
								</div>
							);
						})}
						{results.length > 0 && (
							<button
								onClick={() => { setResults([]); setExpandedResults(new Set()); }}
								style={{
									width: '100%',
									padding: '6px',
									backgroundColor: 'transparent',
									color: '#999',
									border: '1px solid #ddd',
									borderRadius: '4px',
									fontSize: '12px',
									cursor: 'pointer',
									marginTop: '4px'
								}}
							>
								Clear Results
							</button>
						)}
					</div>
				)}
			</div>
			</div>
		</div>
	);
};
